import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
// NOTE: keep this module's imports to CommonModule ONLY, and keep every declared
// file importing nothing heavier than @angular/core. LegacyShimsModule evaluates
// early (it's the first re-export of the account-shared barrel via
// account-shared.module), so adding a module import (e.g. PipesModule) OR a
// declared file that imports app.module (for $injector/$translate) perturbs
// barrel eval order into a circular-import runtime crash ("Cannot read
// properties of undefined") that blanks the WHOLE SPA — the AOT build still
// succeeds, so only a runtime load reveals it. Same fragility as the
// extends-via-barrel TDZ (HANDOFF §6.1). FtCampaignListComponent therefore
// inlines all of v1's campaign filters (campaignName/capitalize/
// arrayFilterByPropertyInArray/share/percentage) as methods instead of adding
// new shim pipes here. See [[project_account_barrel_eval_order_fragility]].
import {
  FtCarouselCardDirective,
  FtCarouselCardMoveLeftOrRightDirective,
  FtCarouselCardTriggerDirective,
  FtCarouselCardsComponent,
  FtCarouselCardsMoveLeftDirective,
  FtCarouselCardsMoveRightDirective,
} from './carousel-cards';
import { FtContestStatusComponent } from './contest-status.component';
import { DateRangePipe, DaysUntilDatePipe } from './date-range.pipe';
import { FtExpandableContentComponent } from './expandable-content.component';
import { FtExpandableSecondaryDirective, FtExpandableSlotDirective } from './expandable-slots';
import { PanelComponent } from './panel.component';
import { FtInitializedContentComponent } from './initialized-content.component';
// FtCampaignListComponent is declared in AdvocacyGroupModule (its only
// consumer), NOT here: it uses the translate/parse pipes, and a pipe-referencing
// component in this EARLY-evaluated barrel module perturbs account-shared barrel
// eval order into a circular-import SPA crash. See
// [[project_account_barrel_eval_order_fragility]].
import { FtTouchpointTriggerDirective } from './modal-triggers';
import { NgPluralizeDirective } from './ng-pluralize.directive';
import { FtNextPageDirective, FtPagingDirective, FtPrevPageDirective } from './paging';
import { FtScrollToDirective, FtPositionalClassDirective } from './scroll';
import { FtStoryHighlightComponent } from './story-highlight.component';
import { FtSectionComponent } from './sections/section.component';
import { FtSectionTriggerDirective } from './sections/section-trigger.directive';
import { FtSectionsComponent } from './sections/sections.component';
import { FtStatComponent } from './stat.component';
import { LegacyParsePipe } from './parse.pipe';
import { FtSetStateDirective, FtStateClassDirective, FtStateDirective } from './states';
import { FtSwitchComponent } from './switch.component';
import { LegacyTranslatePipe } from './translate.pipe';
import { TranslateAttrDirective } from './translate-attr.directive';

/**
 * Native-Angular replacements for the AngularJS leaf-template directives the
 * advocacy/platform leaves lean on (`<ft-sections>` family, `<ft-stat>`,
 * `<ft-initialized-content>`, `<ft-contest-status>`, the `dateRange` filter).
 *
 * Re-exported by `AccountSharedModule` so any feature module that imports
 * AccountSharedModule (which is already standard) gets these in its template
 * scope for free. The AngularJS originals stay registered and active — they
 * serve unmigrated AngularJS templates; when a leaf migrates to a native
 * Angular component, Angular's compiler picks up THESE replacements for the
 * same selectors. No runtime collision because Angular templates use
 * Angular's compiler exclusively and AngularJS templates use AngularJS's.
 *
 * Each shim closes a specific blocker discovered scoping the advocacy
 * contests/bigPicture/audiences/journeys leaf ports. See HANDOFF §6.1.
 */
@NgModule({
  imports: [CommonModule],
  declarations: [
    FtSectionsComponent,
    FtSectionComponent,
    FtSectionTriggerDirective,
    FtCarouselCardsComponent,
    FtCarouselCardDirective,
    FtCarouselCardsMoveLeftDirective,
    FtCarouselCardsMoveRightDirective,
    FtCarouselCardMoveLeftOrRightDirective,
    FtCarouselCardTriggerDirective,
    FtStatComponent,
    FtInitializedContentComponent,
    FtContestStatusComponent,
    FtExpandableContentComponent,
    FtExpandableSlotDirective,
    FtExpandableSecondaryDirective,
    PanelComponent,
    DateRangePipe,
    DaysUntilDatePipe,
    LegacyParsePipe,
    LegacyTranslatePipe,
    TranslateAttrDirective,
    FtStateDirective,
    FtSetStateDirective,
    FtStateClassDirective,
    NgPluralizeDirective,
    FtPagingDirective,
    FtNextPageDirective,
    FtPrevPageDirective,
    FtScrollToDirective,
    FtPositionalClassDirective,
    FtSwitchComponent,
    FtStoryHighlightComponent,
    FtTouchpointTriggerDirective,
  ],
  exports: [
    FtSectionsComponent,
    FtSectionComponent,
    FtSectionTriggerDirective,
    FtCarouselCardsComponent,
    FtCarouselCardDirective,
    FtCarouselCardsMoveLeftDirective,
    FtCarouselCardsMoveRightDirective,
    FtCarouselCardMoveLeftOrRightDirective,
    FtCarouselCardTriggerDirective,
    FtStatComponent,
    FtInitializedContentComponent,
    FtContestStatusComponent,
    FtExpandableContentComponent,
    FtExpandableSlotDirective,
    FtExpandableSecondaryDirective,
    PanelComponent,
    DateRangePipe,
    DaysUntilDatePipe,
    LegacyParsePipe,
    LegacyTranslatePipe,
    TranslateAttrDirective,
    FtStateDirective,
    FtSetStateDirective,
    FtStateClassDirective,
    NgPluralizeDirective,
    FtPagingDirective,
    FtNextPageDirective,
    FtPrevPageDirective,
    FtScrollToDirective,
    FtPositionalClassDirective,
    FtSwitchComponent,
    FtStoryHighlightComponent,
    FtTouchpointTriggerDirective,
  ],
})
export class LegacyShimsModule {}
